<template>
    <div class="navigator-container">
        
        <button 
            class="navigator-button"
            :class="layout.loading || !layout.prev ? 'disabled' : ''"
            @click="prevImage()"
            :disabled="layout.loading || !layout.prev">
            <Skip-backIcon class="w-5 h-5" />
        </button>

        <div class="navigator-counter-container">
            <input 
                type="text"
                class="navigator-input"
                v-model="inputValue"
                @focus="handleInputFocus"
                @blur="handleInputBlur"
                ref="inputRef"
                :disabled="layout.loading"
                :class="{'input-active': isInputActive}"
            />
            <span class="navigator-separator"> / </span>
            <span>{{ layout.totalImages }}</span>
        </div>

        <button 
            class="navigator-button"
            :class="layout.loading || !layout.next ? 'disabled' : ''"
            @click="nextImage()"
            :disabled="layout.loading || !layout.next">
            <Skip-forwardIcon class="w-5 h-5" />
        </button>

    </div>
</template>

<script setup>
import { onMounted, onUnmounted, watch, ref, computed } from "vue";
import { fabric }           from 'fabric'
import * as rosepetalModel  from "rosepetal-model";

const emit = defineEmits(['editImage', 'saveImage']);

let $dataset    = rosepetalModel.default.dataset;
let $image      = rosepetalModel.default.image;

const props = defineProps({
    dataset: { 
        type: Object, 
        required: true 
    },
    image: { 
        type: Object, 
        required: true 
    },
    filters: { 
        type: Object, 
        required: true 
    },
    currentPage: { 
        type: Number, 
        default: 0 
    },
    hasChanges: {
        type: Function,
        default: () => true
    }
});

const inputRef              = ref(null);
const userInputValue        = ref('');
const isInputActive         = ref(false);
const isNavigating          = ref(false);
const targetNavigationIndex = ref(null);

const fabricImgOpt = {
    elementType:    'background',
    hoverCursor:    'grab',
    erasable:       false,
    selectable:     false,
    evented:        false
};

const layout = ref({
    prev:           false,
    next:           false,
    totalImages:    0,
    currentIndex:   0,
    loading:        false,
    images:         [],
    filters:        {},
    currentData:    null
});

const inputValue = computed({
    get: () => {
        if (isNavigating.value && targetNavigationIndex.value) return targetNavigationIndex.value.toString();
        if (isInputActive.value) return userInputValue.value;
        return userInputValue.value !== '' ? userInputValue.value : (layout.value.currentIndex + 1).toString();
    },
    set: (val) => { userInputValue.value = val; }
});

const handleKeyDown = (event) => {
    if (layout.value.loading) return;
    if (event.key === "ArrowRight" && layout.value.next && !isInputActive.value) nextImage();
    else if (event.key === "ArrowLeft" && layout.value.prev && !isInputActive.value) prevImage();
    else if (event.key === "Enter" && isInputActive.value) goToImageIndex();
}

const handleInputFocus = () => {
    isInputActive.value = true;
    if (!userInputValue.value) {
        userInputValue.value = (layout.value.currentIndex + 1).toString();
    }
}

const handleInputBlur = () => {
    isInputActive.value = false;
    userInputValue.value = '';
}

const CACHE_RADIUS  = 10;
const MAX_CACHE     = 20;
const imageCache    = new Map();
const metaCache     = new Map();
const urlCache      = new Map();

function loadFabric(url) {
    return new Promise((resolve, reject) => {
        fabric.Image.fromURL(url, imgObj => {
            resolve(imgObj ? Object.assign(imgObj, fabricImgOpt) : reject('Invalid image'));
        }, { crossOrigin: 'anonymous' });
    });
}

async function fetchStorageUrl(uri) {
    if (urlCache.has(uri)) return urlCache.get(uri);
    
    const gimageObj = await $image.getStorageUrl(uri);
    urlCache.set(uri, gimageObj);
    return gimageObj;
}

async function fetchMetadata(id, reload = false) {
    if (metaCache.has(id) && !reload) return metaCache.get(id);

    let data = await $image.get(id);
    data.gimageObj = await fetchStorageUrl(data.uri);
    
    if (data.gimageObj && data.gimageObj.meta) {
        metaCache.set(id, data);
        if (metaCache.size > MAX_CACHE) {
            const oldest = metaCache.keys().next().value;
            metaCache.delete(oldest);
        }
    }

    return data;
}

function invalidateCache(imageId) {
    metaCache.delete(imageId);
    imageCache.delete(imageId);
}

async function fetchLowResImage(imgData) {
    if (imageCache.has(imgData.id)) return imageCache.get(imgData.id);

    const loadTask = (async () => {
        const { imageUrl } = await $image.loadImageResources(imgData, 'navigator');
        return await loadFabric(imageUrl);
    })();

    imageCache.set(imgData.id, loadTask);

    if (imageCache.size > MAX_CACHE) {
        const oldest = imageCache.keys().next().value;
        imageCache.delete(oldest);
    }

    return loadTask;
}

function updatePreloadWindow(currentIndex) {
    const total = layout.value.images.length;
    const start = Math.max(0, currentIndex - CACHE_RADIUS);
    const end = Math.min(total - 1, currentIndex + CACHE_RADIUS);

    const neededIds = layout.value.images.slice(start, end + 1);

    requestIdleCallback(() => {
        for (const id of neededIds) {
            if (!imageCache.has(id)) {
                fetchMetadata(id).then(async (imgData) => {
                    await fetchLowResImage(imgData);
                }).catch(() => {});
            }
        }

        for (const id of imageCache.keys()) {
            if (!neededIds.includes(id)) {
                imageCache.delete(id);
            }
        }

        for (const id of metaCache.keys()) {
            if (!neededIds.includes(id)) {
                metaCache.delete(id);
            }
        }
    });
}

async function loadImage(index) {
    if (layout.value.loading || index < 0 || index >= layout.value.images.length) return;

    layout.value.loading = true;
    layout.value.currentIndex = index;
    layout.value.prev = index > 0;
    layout.value.next = index < layout.value.images.length - 1;

    try {
        const imgId = layout.value.images[index];
        const imgData = await fetchMetadata(imgId);
        const imgObj = await fetchLowResImage(imgData);
        imgData.fabricImage = imgObj;

        if (imgData && imgObj) {
            layout.value.currentData = imgData;
            updatePreloadWindow(index);
        }
    } catch (err) {
        emit('loadError', err);
    } finally {
        layout.value.loading = false;
    }
}

async function nextImage() {
    if (layout.value.next && !layout.value.loading) {
        if (props.hasChanges()) {
            emit('saveImage');
            invalidateCache(props.image.id);
        }
        layout.value.currentData = null;
        loadImage(layout.value.currentIndex + 1).then(() => {
            if (layout.value.currentData) {
                emit('editImage', layout.value.currentData);
            }
        });
    }
}

async function prevImage() {
    if (layout.value.prev && !layout.value.loading) {
        if (props.hasChanges()) {
            emit('saveImage');
            invalidateCache(props.image.id);
        }
        layout.value.currentData = null;
        loadImage(layout.value.currentIndex - 1).then(() => {
            if (layout.value.currentData) {
                emit('editImage', layout.value.currentData);
            }
        });
    }
}

const goToImageIndex = async () => {
    if (layout.value.loading) return;

    const targetIndex = parseInt(userInputValue.value);
    
    if (isNaN(targetIndex) || targetIndex < 1 || targetIndex > layout.value.totalImages) {
        userInputValue.value = '';
        return;
    }

    if (targetIndex === layout.value.currentIndex + 1) {
        userInputValue.value = '';
        return;
    }

    if (props.hasChanges()) {
        emit('saveImage');
        invalidateCache(props.image.id);
    }
    loadImage(targetIndex - 1).then(() => {
        emit('editImage', layout.value.currentData);
    });

    userInputValue.value = '';
    layout.value.loading = false;
    isNavigating.value = false;
    targetNavigationIndex.value = null;
}

onMounted(async () => {
    window.addEventListener('keydown', handleKeyDown);

    layout.value.filters = { ...props.filters };
    layout.value.filters.limit      = false;
    layout.value.filters.perPage    = false;

    layout.value.images = await $dataset.getImagesIds(props.dataset, layout.value.filters);
    layout.value.totalImages = layout.value.images.length;

    const startIndex = Math.max(0, layout.value.images.indexOf(props.image.id));
    await loadImage(startIndex);
});

onUnmounted(() => {
    window.removeEventListener("keydown", handleKeyDown);
});

watch(() => props.filters, async () => {
    const filtersModified = 
        layout.value.filters.order !== props.filters.order ||
        layout.value.filters.direction !== props.filters.direction ||
        layout.value.filters.objByTag !== props.filters.objByTag ||
        layout.value.filters.objtagsType !== props.filters.objtagsType;
    if (filtersModified) {
        layout.value.filters = { ...props.filters };
        layout.value.images = await $dataset.getImagesIds(props.dataset, props.filters);
        layout.value.totalImages = layout.value.images.length;
        await loadImage(0);
    }
}, { deep: true });

defineExpose({
    nextImage,
    prevImage
});
</script>

<style>
.navigator-container {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0.25rem 0;
    background-color: #f8fafc;
}

.navigator-button {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0.2rem 0.2rem;
    width: 1.5rem;
    height: 1.5rem;
    border-radius: 4px;
    color: var(--primary-600, #1e4591);
    background-color: var(--primary-50, #edf2fa);
    border: 1px solid transparent;
    transition: all 0.2s ease;
}

.navigator-button:hover:not(.disabled) {
    background-color: var(--primary-100, #d2dff0);
    color: var(--primary-700, #17356e);
    border-color: var(--primary-200, #a7bfe4);
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.navigator-button:active:not(.disabled) {
    background-color: var(--primary-200, #a7bfe4);
}

.navigator-button.disabled {
    opacity: 0.5;
    cursor: not-allowed;
    background-color: #f1f5f9;
    color: #94a3b8;
    border-color: transparent;
}

.navigator-counter-container {
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0.25rem 16px;
    font-size: 0.9rem;
    font-weight: 500;
    color: var(--primary-700, #17356e);
    min-width: 100px;
    text-align: center;
}

.navigator-input {
    width: 42px;
    text-align: center;
    background-color: white;
    border: 1px solid var(--primary-200, #a7bfe4);
    border-radius: 4px;
    color: var(--primary-700, #17356e);
    font-weight: 500;
    padding: 2px 4px;
    height: 24px;
    margin: 0 4px;
    transition: all 0.2s ease;
    cursor: text;
}

.navigator-input:hover:not(:disabled) {
    border-color: var(--primary-400, #4a77c5);
}

.navigator-input:focus, .input-active {
    outline: none;
    border-color: var(--primary-500, #2d5dab);
    box-shadow: 0 0 0 1px rgba(30, 69, 145, 0.2);
}

.navigator-input:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    border-color: #cbd5e1;
}

.navigator-separator {
    margin: 0 4px;
}
</style>